// 📦 Componente para generación en lote de facturas internas
// Se integra con StandardListView para acciones bulk
import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  ReceiptLong as InvoiceIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useInternalInvoiceGeneration } from "@/hooks/use-internal-invoice-generation";
import type { BatchGenerateResultItem } from "@/types/internal-invoices";
interface BulkGenerateInternalInvoicesProps {
  selectedIds: number[];
  onSuccess?: () => void;
  onClose?: () => void;
  trigger?: React.ReactNode; // Elemento que activa el diálogo
}
/**
 * Modal para generación en lote de facturas internas
 * Muestra progreso y resultados detallados
 */
export function BulkGenerateInternalInvoices({
  selectedIds,
  onSuccess,
  onClose,
  trigger,
}: BulkGenerateInternalInvoicesProps) {
  const { batchGeneration, generateBatch, resetBatchState } = useInternalInvoiceGeneration();
  const [open, setOpen] = useState(false);
  const { isGenerating, result } = batchGeneration;
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetBatchState();
    if (onClose) onClose();
  };
  const handleGenerate = async () => {
    if (selectedIds.length === 0) return;
    try {
      await generateBatch(selectedIds);
    } catch (error) {
      // El hook ya maneja los errores
    }
  };
  const handleSuccess = () => {
    handleClose();
    if (onSuccess) onSuccess();
  };
  // Calcular estadísticas si hay resultado
  const stats = result ? {
    total: result.totalRequested,
    success: result.totalSuccess,
    failed: result.totalFailed,
    successRate: result.totalRequested > 0 ? (result.totalSuccess / result.totalRequested) * 100 : 0,
  } : null;
  return (
    <>
      {/* Trigger personalizado o botón por defecto */}
      {trigger ? (
        <div onClick={handleOpen}>{trigger}</div>
      ) : (
        <Button
          variant="contained"
          onClick={handleOpen}
          disabled={selectedIds.length === 0}
          startIcon={<InvoiceIcon />}
        >
          Generar Facturas Internas ({selectedIds.length})
        </Button>
      )}
      {/* Diálogo principal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={isGenerating}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InvoiceIcon color="primary" />
            <Typography variant="h6">
              Generar Facturas Internas en Lote
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Información inicial */}
          {!result && (
            <Box mb={3}>
              <Typography variant="body1" gutterBottom>
                Se generarán facturas internas para las siguientes facturas de proveedor:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {selectedIds.map((id) => (
                  <Chip
                    key={id}
                    label={`Factura ${id}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Alert severity="warning">
                <AlertTitle>Rate automatico del sistema</AlertTitle>
                <Typography variant="body2">
                  La generacion en lote usa unicamente el rate automatico configurado en el sistema.
                  No permite cargar rate manual ni motivo de auditoria. Si no existe rate automatico para una factura,
                  esa factura fallara individualmente y las demas continuaran. Todas las facturas generadas quedaran en estado "Draft".
                </Typography>
              </Alert>
            </Box>
          )}
          {/* Progreso */}
          {isGenerating && (
            <Box mb={3}>
              <Typography variant="body2" gutterBottom>
                Procesando {selectedIds.length} facturas...
              </Typography>
              <LinearProgress />
            </Box>
          )}
          {/* Resultados */}
          {result && (
            <Box>
              {/* Resumen general */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Resultado del Lote
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                  <Chip
                    icon={<InvoiceIcon />}
                    label={`Total: ${stats?.total}`}
                    variant="outlined"
                  />
                  <Chip
                    icon={<SuccessIcon />}
                    label={`Exitosas: ${stats?.success}`}
                    color="success"
                    variant="filled"
                  />
                  <Chip
                    icon={<ErrorIcon />}
                    label={`Fallidas: ${stats?.failed}`}
                    color="error"
                    variant="filled"
                  />
                </Box>
                {/* Barra de progreso visual */}
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tasa de éxito: {stats?.successRate.toFixed(1)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={stats?.successRate || 0}
                    color={stats?.successRate === 100 ? "success" : stats?.successRate === 0 ? "error" : "warning"}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Box>
              {/* Detalle por factura */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Detalle por Factura
                </Typography>
                <List dense>
                  {result.results.map((item: BatchGenerateResultItem, index: number) => (
                    <ListItem key={index} divider>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.success ? (
                          <SuccessIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight="bold">
                              Factura Proveedor {item.carrierInvoiceId}
                            </Typography>
                            {item.success && item.internalInvoiceId && (
                              <Chip
                                size="small"
                                label={`ID: ${item.internalInvoiceId}`}
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {item.success ? (
                              <Typography variant="body2" color="success.main">
                                Generada exitosamente - {item.linesCreated} línea(s) - €{item.totalAmount.toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="error.main">
                                Error: {item.error}
                              </Typography>
                            )}
                            {/* Warnings */}
                            {item.warnings?.length > 0 && (
                              <Box mt={1}>
                                {item.warnings.map((warning, wIndex) => (
                                  <Typography key={wIndex} variant="caption" color="warning.main" display="block">
                                    ⚠️ {warning}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!result && (
            <>
              <Button onClick={handleClose} disabled={isGenerating}>
                Cancelar
              </Button>
              <Button
                onClick={handleGenerate}
                variant="contained"
                disabled={isGenerating}
                startIcon={isGenerating ? undefined : <InvoiceIcon />}
              >
                {isGenerating ? "Procesando..." : `Generar ${selectedIds.length} Facturas`}
              </Button>
            </>
          )}
          {result && (
            <Button onClick={handleSuccess} variant="contained">
              Aceptar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}










